import { ANNUL_DEAL_REASON_MIN, type CatalogItemDto, type DealItem } from '@performance-crm/shared';
import { Loader2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { NextStepFields } from '@/components/leads/NextStepFields';
import { emptyNextStep, toNextStep, type NextStepForm } from '@/components/leads/nextStep';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast-context';
import { todayInTz } from '@/lib/format';
import { useAnnulDeal, useCatalogs, useRefundDeal } from '@/lib/queries';
import { useCompanyTimezone } from '@/lib/useCompanyTimezone';
import {
  emptyUndoForm,
  multipleLiveDealsNotice,
  refundInstant,
  undoErrorMessage,
  type UndoForm,
} from './undoForm';

export type DealActionMode = 'refund' | 'annul';

const isTechnical = (r: CatalogItemDto) => r.metadata.isTechnical === true;
/** Motivos que pone solo el sistema (p. ej. «Caída sin registrar» del saneamiento de unidades dobles). */
const isSystem = (r: CatalogItemDto) => r.metadata.isSystem === true;

/** Campos compartidos de devolución / anulación (también los usa el retroceso del kanban). */
export function DealUndoFields({
  mode,
  onModeChange,
  value,
  onChange,
  dropReasons,
  idPrefix,
  tz,
}: {
  mode: DealActionMode;
  onModeChange?: (m: DealActionMode) => void;
  value: UndoForm;
  onChange: (v: UndoForm) => void;
  dropReasons: CatalogItemDto[];
  idPrefix: string;
  /** Zona de la empresa: tope «hoy» del campo de fecha. */
  tz?: string;
}) {
  const set = (patch: Partial<typeof value>) => onChange({ ...value, ...patch });
  return (
    <div className="space-y-3">
      {onModeChange && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onModeChange('refund')}
            className={`flex-1 rounded-md border px-3 py-1.5 text-sm ${mode === 'refund' ? 'border-primary bg-primary text-primary-foreground' : 'bg-card'}`}
          >
            Devolución (el cliente se cayó)
          </button>
          <button
            type="button"
            onClick={() => onModeChange('annul')}
            className={`flex-1 rounded-md border px-3 py-1.5 text-sm ${mode === 'annul' ? 'border-destructive bg-destructive text-destructive-foreground' : 'bg-card'}`}
          >
            Anulación (se registró por error)
          </button>
        </div>
      )}
      {mode === 'refund' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-date`}>Fecha de devolución</Label>
            <Input
              id={`${idPrefix}-date`}
              type="date"
              value={value.refundedAt}
              max={todayInTz(tz)}
              onChange={(e) => set({ refundedAt: e.target.value })}
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-reason`}>Motivo de caída</Label>
            <Select
              id={`${idPrefix}-reason`}
              value={value.dropReason}
              onChange={(e) => set({ dropReason: e.target.value })}
              required
            >
              <option value="">Selecciona…</option>
              {dropReasons
                .filter((r) => !isTechnical(r) && !isSystem(r))
                .map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
            </Select>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor={`${idPrefix}-notes`}>Notas</Label>
            <Textarea
              id={`${idPrefix}-notes`}
              rows={2}
              value={value.notes}
              onChange={(e) => set({ notes: e.target.value })}
            />
          </div>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor={`${idPrefix}-why`}>¿Por qué se anula?</Label>
            <Input
              id={`${idPrefix}-why`}
              value={value.reason}
              onChange={(e) => set({ reason: e.target.value })}
              required
              minLength={ANNUL_DEAL_REASON_MIN}
              placeholder="Ej. se registró en el lead equivocado"
            />
            <p className="text-muted-foreground text-xs">
              Explica el motivo (al menos {ANNUL_DEAL_REASON_MIN} caracteres): la anulación borra el
              proceso del historial y queda en la auditoría.
            </p>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor={`${idPrefix}-tech`}>Motivo técnico (opcional)</Label>
            <Select
              id={`${idPrefix}-tech`}
              value={value.techReason}
              onChange={(e) => set({ techReason: e.target.value })}
            >
              <option value="">—</option>
              {dropReasons
                .filter((r) => isTechnical(r) && !isSystem(r))
                .map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
            </Select>
          </div>
          <p className="text-muted-foreground text-xs sm:col-span-2">
            El proceso anulado no cuenta en ninguna métrica (ni bruta ni activa) y libera las
            unidades.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Devolución o anulación de un proceso concreto desde la ficha. `canAnnul` = quien gestiona la empresa (solo él
 * anula; devolver también puede el asesor dueño). `initialNext = 'DESIST'` es «Devolver y desistir»: la devolución
 * y el desistimiento salen en un solo paso (Lote 5 · H1). `liveDealCount` = procesos vivos del lead: con más de
 * uno, «Desistir» como siguiente paso no cabe (al lead le quedaría otro proceso vivo y la API revertiría todo), así
 * que el diálogo lo avisa y solo registra la devolución o anulación de este proceso.
 */
export function DealActionDialog({
  companyId,
  deal,
  mode: initialMode,
  canAnnul = true,
  initialNext = 'TASK',
  liveDealCount = 1,
  onOpenChange,
}: {
  companyId: string;
  deal: DealItem | null;
  mode: DealActionMode;
  canAnnul?: boolean;
  initialNext?: NextStepForm['kind'];
  liveDealCount?: number;
  onOpenChange: (o: boolean) => void;
}) {
  const { data: catalogs } = useCatalogs(companyId);
  const tz = useCompanyTimezone(companyId);
  const refund = useRefundDeal();
  const annul = useAnnulDeal();
  const toast = useToast();
  const [mode, setMode] = useState<DealActionMode>(initialMode);
  const [form, setForm] = useState(() => emptyUndoForm(tz));
  const [next, setNext] = useState<NextStepForm>(emptyNextStep(tz));
  const [error, setError] = useState<string | null>(null);
  const allowDesist = liveDealCount <= 1;
  // Los valores por defecto se reinician cada vez que se abre (con «hoy» de la zona de la empresa).
  useEffect(() => {
    if (deal) {
      setMode(canAnnul ? initialMode : 'refund');
      setForm(emptyUndoForm(tz));
      setNext({ ...emptyNextStep(tz), kind: allowDesist ? initialNext : 'TASK' });
      setError(null);
    }
  }, [deal, initialMode, initialNext, canAnnul, allowDesist, tz]);
  if (!deal) return null;
  const label = deal.type === 'VENTA' ? 'venta' : 'separación';
  const desist = initialNext === 'DESIST' && allowDesist;
  const pending = refund.isPending || annul.isPending;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const lead =
        mode === 'refund'
          ? await refund.mutateAsync({
              id: deal.id,
              input: {
                refundedAt: refundInstant(form.refundedAt, tz),
                dropReason: form.dropReason,
                notes: form.notes || null,
                next: toNextStep(next, tz),
              },
            })
          : await annul.mutateAsync({
              id: deal.id,
              input: {
                reason: form.reason,
                // Solo el motivo técnico elegido en este modo: el de la devolución no viaja (P26).
                dropReason: form.techReason || null,
                next: toNextStep(next, tz),
              },
            });
      // «y lead desistido» solo si la API lo confirma (el desistimiento corre en la misma transacción).
      const done =
        mode === 'refund'
          ? `Devolución de ${label} registrada`
          : `${deal.type === 'VENTA' ? 'Venta' : 'Separación'} anulada`;
      toast.success(lead.hasDesisted ? `${done} y lead desistido` : done);
      onOpenChange(false);
    } catch (err) {
      setError(undoErrorMessage(err, 'No se pudo registrar'));
    }
  };
  return (
    <Dialog open={!!deal} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>
              {desist ? 'Devolver y desistir' : mode === 'refund' ? 'Devolución' : 'Anulación'}
              {desist ? ` · ${label}` : ` de ${label}`}
              {deal.units.length ? ` · ${deal.units.map((u) => u.code).join(', ')}` : ''}
            </DialogTitle>
            <DialogDescription>
              {desist
                ? 'Un lead con separación o venta viva no se desiste directamente: primero se registra la devolución (la caída queda contada y la unidad vuelve al stock) y en el mismo paso se desiste el lead.'
                : 'La devolución mantiene el proceso en bruto y lo saca de los activos; la anulación lo excluye de todo. En ambos casos las unidades vuelven al stock y el lead retrocede de etapa.'}
            </DialogDescription>
          </DialogHeader>
          {!allowDesist && (
            <p
              className="border-warning/40 bg-warning/10 rounded-md border px-3 py-2 text-sm"
              data-testid="deal-multiple-live"
            >
              {multipleLiveDealsNotice(liveDealCount)}
            </p>
          )}
          <DealUndoFields
            mode={mode}
            onModeChange={canAnnul ? setMode : undefined}
            value={form}
            onChange={setForm}
            dropReasons={catalogs?.dropReasons ?? []}
            idPrefix="deal"
            tz={tz}
          />
          <NextStepFields
            value={next}
            onChange={setNext}
            desistReasons={catalogs?.desistReasons ?? []}
            idPrefix="deal-next"
            allowDesist={allowDesist}
          />
          {error && (
            <p
              role="alert"
              className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
            >
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant={mode === 'annul' ? 'destructive' : 'default'}
              disabled={pending}
              data-testid="deal-action-submit"
            >
              {pending && <Loader2 className="h-4 w-4 animate-spin" />}{' '}
              {next.kind === 'DESIST' && allowDesist
                ? mode === 'refund'
                  ? 'Devolver y desistir'
                  : 'Anular y desistir'
                : mode === 'refund'
                  ? 'Registrar devolución'
                  : 'Anular proceso'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
